import client from "./client";

export type ExpenseCategory =
  | "travel"
  | "meals"
  | "office"
  | "equipment"
  | "software"
  | "other";

export type ExpenseStatus =
  | "draft"
  | "submitted"
  | "approved"
  | "rejected"
  | "reimbursed";

export interface ExpenseItem {
  id: string;
  expense_report_id: string;
  description: string;
  category: ExpenseCategory;
  amount: number;
  currency: string;
  item_date: string;
  merchant_name: string | null;
  receipt_url: string | null;
  notes: string | null;
  created_at: string;
}

export interface ExpenseItemInput {
  description: string;
  category: ExpenseCategory;
  amount: number;
  currency?: string;
  item_date: string;
  merchant_name?: string | null;
  notes?: string | null;
}

export interface ExpenseItemUpdateInput {
  description?: string;
  category?: ExpenseCategory;
  amount?: number;
  currency?: string;
  item_date?: string;
  merchant_name?: string | null;
  notes?: string | null;
}

export interface ExpenseReport {
  id: string;
  employee_id: string;
  employee_name: string | null;
  department_name: string | null;
  title: string;
  description: string | null;
  status: ExpenseStatus;
  total_amount: number;
  currency: string;
  submitted_at: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
  reimbursed_at: string | null;
  items: ExpenseItem[];
  created_at: string;
  updated_at: string;
}

export interface ExpenseReportCreateInput {
  title: string;
  description?: string | null;
  currency?: string;
  items?: ExpenseItemInput[];
}

export interface ExpensePolicy {
  id: string;
  org_id: string;
  category: ExpenseCategory;
  max_amount_per_item: number | null;
  max_amount_per_month: number | null;
  requires_receipt_above: number | null;
  auto_approve_below: number | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface ExpensePolicyUpsertInput {
  category: ExpenseCategory;
  max_amount_per_item?: number | null;
  max_amount_per_month?: number | null;
  requires_receipt_above?: number | null;
  auto_approve_below?: number | null;
  notes?: string | null;
}

export interface ExpenseDashboard {
  currency: string;
  pending_amount: number;
  pending_count: number;
  approved_amount: number;
  approved_count: number;
  reimbursed_amount: number;
  reimbursed_count: number;
  by_department: Array<{
    department_id: string | null;
    department_name: string | null;
    employee_count: number;
    total_amount: number;
  }>;
  by_category: Array<{
    category: string;
    count: number;
    total_amount: number;
  }>;
  by_month: Array<{
    month: string;
    total_amount: number;
    report_count: number;
  }>;
}

export async function listMyReports(): Promise<ExpenseReport[]> {
  const res = await client.get("/expenses/reports");
  return res.data;
}

export async function readReport(id: string): Promise<ExpenseReport> {
  const res = await client.get(`/expenses/reports/${id}`);
  return res.data;
}

export async function createReport(
  input: ExpenseReportCreateInput,
): Promise<ExpenseReport> {
  const res = await client.post("/expenses/reports", input);
  return res.data;
}

export async function addItem(
  reportId: string,
  input: ExpenseItemInput,
): Promise<ExpenseItem> {
  const res = await client.post(`/expenses/reports/${reportId}/items`, input);
  return res.data;
}

export async function updateItem(
  itemId: string,
  input: ExpenseItemUpdateInput,
): Promise<ExpenseItem> {
  const res = await client.put(`/expenses/items/${itemId}`, input);
  return res.data;
}

export async function deleteItem(itemId: string): Promise<void> {
  await client.delete(`/expenses/items/${itemId}`);
}

export async function uploadReceipt(
  itemId: string,
  file: File,
): Promise<ExpenseItem> {
  const form = new FormData();
  form.append("file", file);
  const res = await client.post(`/expenses/items/${itemId}/receipt`, form);
  return res.data;
}

export interface ScanReceiptResult {
  /** The newly-created (or updated) line item, ready to render. */
  item: ExpenseItem;
  /** Report the item landed on (existing draft, or freshly opened). */
  report_id: string;
  report_status: ExpenseStatus;
  /** Raw OCR output so the SPA can show "we read this off the
   *  receipt — confirm or edit?" before the user submits. */
  ocr: {
    merchant_name: string | null;
    item_date: string;
    amount: number | null;
    currency: string;
    tax_amount: number | null;
    tip_amount: number | null;
    category: ExpenseCategory;
    description: string;
    line_items: { description: string; amount: number | null }[];
    confidence: number;
    reason: string | null;
  };
  /** True if the AI's confidence is low or amount couldn't be read.
   *  The SPA should highlight the line item so the user verifies it. */
  needs_review: boolean;
}

export async function scanReceipt(
  file: File,
  reportId?: string,
): Promise<ScanReceiptResult> {
  const form = new FormData();
  form.append("file", file);
  const url = reportId
    ? `/expenses/scan-receipt?report_id=${encodeURIComponent(reportId)}`
    : `/expenses/scan-receipt`;
  const res = await client.post(url, form);
  return res.data;
}

export async function submitReport(id: string): Promise<ExpenseReport> {
  const res = await client.post(`/expenses/reports/${id}/submit`);
  return res.data;
}

export async function reviewReport(
  id: string,
  approve: boolean,
  notes?: string,
): Promise<ExpenseReport> {
  const res = await client.put(`/expenses/reports/${id}/approve`, {
    approve,
    notes: notes || null,
  });
  return res.data;
}

export async function reimburseReport(id: string): Promise<ExpenseReport> {
  const res = await client.put(`/expenses/reports/${id}/reimburse`);
  return res.data;
}

export async function listPending(): Promise<ExpenseReport[]> {
  const res = await client.get("/expenses/pending");
  return res.data;
}

export async function fetchDashboard(): Promise<ExpenseDashboard> {
  const res = await client.get("/expenses/dashboard");
  return res.data;
}

export async function listPolicies(): Promise<ExpensePolicy[]> {
  const res = await client.get("/expenses/policies");
  return res.data;
}

export async function upsertPolicy(
  input: ExpensePolicyUpsertInput,
): Promise<ExpensePolicy> {
  const res = await client.put("/expenses/policies", input);
  return res.data;
}
